import {
  DragDropModule,
  CdkDragDrop,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { Location } from '@angular/common';
import {
  ActivatedRoute,
  NavigationStart,
  Router,
  RouterModule,
} from '@angular/router';
import {
  RdsBadgeModule,
  RdsButtonModule,
  RdsCheckboxModule,
  RdsChipsModule,
  RdsDialogService,
  RdsDropdownModule,
  RdsFormFieldModule,
  RdsIconsModule,
  RdsLinkModule,
  RdsListItemModule,
  RdsProgressSpinnerComponent,
  RdsStepperModule,
  RdsTableDataSource,
  RdsTableModule,
  RdsUploadModule,
} from '@rds/angular-components';
import { OfferingsService } from '../../../services/offerings.service';
import { mapMulesoftKnowledgeToFocusForm } from '../../../mappers/mulesoft-offering-form.mapper';
import { FocusUserInstructionDialogComponent } from '../dialogs/focus-user-instruction-dialog/focus-user-instruction-dialog.component';
import {
  KnowledgeAdditionalResource,
  KnowledgeBestPractice,
  KnowledgeFaq,
  KnowledgeIntegration,
  KnowledgeLearningResource,
  KnowledgeReleaseNote,
  KnowledgeSuccessStory,
  KnowledgeTechnicalResource,
  KnowledgeUserInstruction,
} from '../../../models/knowledge';
import { FocusBestPracticeDialogComponent } from '../dialogs/focus-best-practice-dialog/focus-best-practice-dialog.component';
import { UiService } from '../../../services/ui.service';
import { MulesoftService } from '../../../services/mulesoft.service';
import { buildMulesoftKnowledgeDTO } from '../../../mappers/mulesoft.mapper';
import { FocusLearningResourceDialogComponent } from '../dialogs/focus-learning-resource-dialog/focus-learning-resource-dialog.component';
import { FocusTechnicalResourceDialogComponent } from '../dialogs/focus-technical-resource-dialog/focus-technical-resource-dialog.component';
import { FocusIntegrationDialogComponent } from '../dialogs/focus-integration-dialog/focus-integration-dialog.component';
import { FocusFaqDialogComponent } from '../dialogs/focus-faq-dialog/focus-faq-dialog.component';
import { FocusAdditionalResourceDialogComponent } from '../dialogs/focus-additional-resource-dialog/focus-additional-resource-dialog.component';
import { FocusSuccessStoryDialogComponent } from '../dialogs/focus-success-story-dialog/focus-success-story-dialog.component';
import { FocusReleaseNoteDialogComponent } from '../dialogs/focus-release-note-dialog/focus-release-note-dialog.component';
import { BUTTON_TEXTS } from '../../../constants/buttons.constants';
import { ERRORS_TEXTS } from '../../../constants/errors.contstants';
import { KNOWLEDGE_FORM_TEXTS } from '../../../constants/knowledge-form.constants';
import { AnalyticsService } from '../../../services/analytics.service';
import { OFFERING_TYPE_ROUTE } from '../../../constants/offering-routes.constants';
import { filter, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { OrderByDisplayPipe } from '../../../pipes/order-by-display.pipe';
import { RichTextEditorComponent } from '../../shared/rich-text-editor/rich-text-editor.component';
import { SafeHtmlPipe } from '../../../pipes/safe-html.pipe';
import { attachDirtyDialogGuard } from '../../../guards/dirty-dialog.guard';
import { POPUPS } from '../../../constants/popups.constants';
import { isDuplicateNameError } from '../../../util/duplicate-name-error.util';
import {
  MulesoftOffering,
  MulesoftKnowledgeContent,
} from '../../../models/mulesoft-offering';
import { FormSkeletonComponent } from '../form-skeleton/form-skeleton.component';
@Component({
  selector: 'inexp-focus-knowledge-form',
  imports: [
    DragDropModule,
    RdsIconsModule,
    RdsButtonModule,
    RdsStepperModule,
    ReactiveFormsModule,
    RdsFormFieldModule,
    RdsUploadModule,
    RdsCheckboxModule,
    RdsTableModule,
    RdsDropdownModule,
    RdsListItemModule,
    RdsChipsModule,
    RdsLinkModule,
    RouterModule,
    RdsProgressSpinnerComponent,
    OrderByDisplayPipe,
    RichTextEditorComponent,
    SafeHtmlPipe,
    RdsBadgeModule,
    FormSkeletonComponent,
  ],
  templateUrl: './focus-knowledge-form.component.html',
  styleUrl: './focus-knowledge-form.component.scss',
  host: {
    '(window:beforeunload)': 'unloadNotification($event)',
  },
})
export class FocusKnowledgeFormComponent implements OnInit, OnDestroy {
  private readonly _route = inject(ActivatedRoute);
  private readonly _offeringsService = inject(OfferingsService);
  private _knowledgeLoaded = false;
  private readonly _fb = inject(FormBuilder);
  private readonly _dialog = inject(RdsDialogService);
  private readonly _uiService = inject(UiService);
  private readonly _mulesoftService = inject(MulesoftService);
  private readonly _router = inject(Router);
  private readonly _location = inject(Location);
  private readonly _paramMap = toSignal(this._route.paramMap);
  private readonly _queryParamMap = toSignal(this._route.queryParamMap);
  private readonly _analyticsService = inject(AnalyticsService);
  private readonly destroy$ = new Subject<void>();
  private readonly _localKnowledgeUuid = signal<string | null>(null);
  readonly knowledge = signal<MulesoftKnowledgeContent | null>(null);
  readonly userInstructionColumns = ['publish', 'title', 'description'];
  readonly bestPracticeColumns = ['publish', 'title', 'description'];
  readonly learningResourceColumns = ['publish', 'title', 'url'];
  readonly technicalResourceColumns = ['publish', 'title', 'url'];
  readonly integrationColumns = [
    'publish',
    'title',
    'description',
    'urlName',
    'url',
  ];
  readonly faqColumns = ['publish', 'question', 'answer'];
  readonly additionalResourceColumns = ['publish', 'title', 'url'];
  readonly successStoryColumns = ['publish', 'title', 'description'];
  readonly releaseNoteColumns = [
    'publish',
    'header',
    'subheader',
    'description',
    'url',
  ];
  readonly faq = this.createResourceManager<KnowledgeFaq>();
  readonly userInstructions =
    this.createResourceManager<KnowledgeUserInstruction>();
  readonly bestPractices = this.createResourceManager<KnowledgeBestPractice>();
  readonly learningResources =
    this.createResourceManager<KnowledgeLearningResource>();
  readonly technicalResources =
    this.createResourceManager<KnowledgeTechnicalResource>();
  readonly integrations = this.createResourceManager<KnowledgeIntegration>();
  readonly additionalResources =
    this.createResourceManager<KnowledgeAdditionalResource>();
  readonly successStories = this.createResourceManager<KnowledgeSuccessStory>();
  readonly releaseNotes = this.createResourceManager<KnowledgeReleaseNote>();

  readonly KNOWLEDGE_FORM = KNOWLEDGE_FORM_TEXTS;
  readonly BUTTONS = BUTTON_TEXTS;
  readonly ERRORS = ERRORS_TEXTS;
  readonly POPUPS = POPUPS;

  readonly knowledgePublishedAt = signal<string | null>(null);
  readonly knowledgeUpdatedAt = signal<string | null>(null);

  readonly offeringUuid = computed(
    () => this._queryParamMap()?.get('offering') || '',
  );
  readonly offering = signal<MulesoftOffering | null>(null);
  readonly knowledgeUuid = computed(() =>
    this._paramMap()?.get('uuid') ?? this._localKnowledgeUuid(),
  );
  isPublishing = signal(false);
  isDraftSaving = signal(false);
  isLoading = computed(() => this._offeringsService.isLoadingDraftOfferings());
  /**
   * Edit mode + knowledge not yet loaded → show form skeleton. Gated on the
   * loaded knowledge (not the list-loading flag) so a save/publish never flashes
   * the skeleton over the populated form.
   */
  readonly showSkeleton = computed(
    () => !!this.knowledgeUuid() && !this.knowledge(),
  );

  /**
   * Edit mode but the numeric knowledge id hasn't loaded yet. Updates key the
   * path on `knowledge.id`, so saving before it resolves would PUT to a bad
   * path (or wrongly create a duplicate) — disable save/publish until it's here.
   */
  readonly isKnowledgeIdLoading = computed(
    () => !!this.knowledgeUuid() && !this.knowledge()?.id,
  );

  private hasLoggedEntry = false;
  private _isKnowledgeAbandonNavigateLogged = false;
  private _isKnowledgeAbandonCloseLogged = false;
  private _isKnowledgeSavedOrPublished = false;
  userInstructionDataSource: any;

  ngOnInit(): void {
    this.setupRouteChangeTracking();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._analyticsService.resetKnowledge();
  }

  loadEffect = effect(() => {
    const knowledgeUuid = this.knowledgeUuid();
    const offeringUuid = this.offeringUuid();

    if (knowledgeUuid != null) {
      this.loadKnowledge(knowledgeUuid);
    } else {
      untracked(() => this.setInitialKnowledgeValues());
      if (offeringUuid != null) {
        this.fetchOfferingByUuid(offeringUuid);
      }
    }

    if (!this.hasLoggedEntry) {
      this.hasLoggedEntry = true;
    }
  });

  // Resolve the parent offering (from the cached draft set) when creating new knowledge
  private fetchOfferingByUuid(offeringUuid: string) {
    const offering = this._offeringsService.getDraftOfferingByUuid(offeringUuid);
    if (offering) {
      this.offering.set(offering);
    }
  }

  unloadNotification($event: BeforeUnloadEvent): string | void {
    if (this.formKnowledge.dirty) {
      $event.preventDefault();

      if ('returnValue' in $event) {
        ($event as any).returnValue = '';
      }

      return '';
    }
  }

  canDeactivate(): boolean {
    if (this._isKnowledgeSavedOrPublished) {
      return true;
    }
    if (this.formKnowledge.dirty) {
      return confirm(
        'You have unsaved changes. Do you really want to leave without saving?',
      );
    }
    return true;
  }

  private markKnowledgeAsSavedOrPublished(): void {
    this._isKnowledgeSavedOrPublished = true;
  }

  /**
   * Overlay the just-saved knowledge onto its parent offering in the cached draft
   * set (no re-fetch). Keeps the knowledge↔offering link resolvable in-session so
   * re-entering `/focus/knowledge/:uuid` still finds the parent via
   * `getDraftOfferingByKnowledgeUuid` / `getDraftKnowledgeByUuid`.
   */
  private mergeSavedKnowledge(
    offering: MulesoftOffering,
    response: { knowledge?: { id?: string; uuid?: string } } | null,
  ): void {
    const savedUuid =
      response?.knowledge?.uuid ?? this.knowledge()?.uuid ?? this.knowledgeUuid();
    this._offeringsService.upsertDraftOptimistic({
      ...offering,
      knowledge: savedUuid,
      knowledgeContent: {
        ...(offering.knowledgeContent ?? {}),
        ...(this.knowledge() ?? {}),
        uuid: savedUuid ?? undefined,
        id: response?.knowledge?.id ?? this.knowledge()?.id,
      },
    });
  }

  private _initializeForm() {
    return this._fb.group(
      {
        userInstructionLongDescription: [''],
        integrationLongDescription: [''],
        userInstructionsVisible: [false],
        bestPracticesVisible: [false],
        learningResourcesVisible: [false],
        technicalResourcesVisible: [false],
        integrationsVisible: [false],
        faqVisible: [false],
        additionalResourcesVisible: [false],
        successStoriesVisible: [false],
        releaseNotesVisible: [false],
        userInstructions: [[] as KnowledgeUserInstruction[]],
        bestPractices: [[] as KnowledgeBestPractice[]],
        learningResources: [[] as KnowledgeLearningResource[]],
        technicalResources: [[] as KnowledgeTechnicalResource[]],
        integrations: [[] as KnowledgeIntegration[]],
        faq: [[] as KnowledgeFaq[]],
        additionalResources: [[] as KnowledgeAdditionalResource[]],
        successStories: [[] as KnowledgeSuccessStory[]],
        releaseNotes: [[] as KnowledgeReleaseNote[]],
      },
      { validators: this.conditionalArrayValidator() },
    );
  }

  formKnowledge = this._initializeForm();

  loadKnowledge(knowledgeUuid: string) {
    // Resolve the parent offering from the cached draft set (the route only
    // carries the knowledge uuid); this is a no-op until the offerings signal
    // resolves, and the effect re-runs when it does.
    const offering =
      this._offeringsService.getDraftOfferingByKnowledgeUuid(knowledgeUuid);
    if (!offering || this._knowledgeLoaded) {
      return;
    }
    this._knowledgeLoaded = true;

    // Fetch the offering directly: the single-offering endpoint returns the
    // complete embedded knowledgeContent (incl. its id), which the list embed
    // omits — without the id, editing knowledge would PUT to `/knowledges/`.
    this._offeringsService
      .fetchDraftOffering(offering.uuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe((fresh) => {
        const parent = fresh ?? offering;
        this.offering.set(parent);

        const knowledgeContent = parent.knowledgeContent;
        if (!knowledgeContent) {
          return;
        }
        this.applyKnowledge(knowledgeContent);
      });
  }

  private applyKnowledge(knowledgeContent: MulesoftKnowledgeContent): void {
    this.knowledgePublishedAt.set(knowledgeContent.publishedAt || null);
    this.knowledgeUpdatedAt.set(knowledgeContent.updatedAt || null);

    const knowledgeForm = mapMulesoftKnowledgeToFocusForm(knowledgeContent);
    this.knowledge.set(knowledgeContent);
    this.formKnowledge.patchValue(knowledgeForm, { emitEvent: false });
    this.loadResources(knowledgeForm);
    //tracking
    this.setInitialKnowledgeValues();
    this.formKnowledge.markAsPristine();
  }

  private loadResources(knowledgeForm: any) {
    this.userInstructions.set(knowledgeForm.userInstructions ?? []);
    this.bestPractices.set(knowledgeForm.bestPractices ?? []);
    this.learningResources.set(knowledgeForm.learningResources ?? []);
    this.technicalResources.set(knowledgeForm.technicalResources ?? []);
    this.integrations.set(knowledgeForm.integrations ?? []);
    this.faq.set(knowledgeForm.faq ?? []);
    this.additionalResources.set(knowledgeForm.additionalResources ?? []);
    this.successStories.set(knowledgeForm.successStories ?? []);
    this.releaseNotes.set(knowledgeForm.releaseNotes ?? []);
  }

  private createResourceManager<T>(initialData: T[] = []) {
    const data = signal<T[]>(initialData);
    const dataSource = new RdsTableDataSource<T>().init();

    const syncDataSource = () => {
      dataSource.data = data();
    };

    return {
      data,
      dataSource,
      update: (item: T | null, newData: T) => {
        data.update((arr) => {
          if (item) {
            const index = arr.indexOf(item);
            if (index > -1) {
              const updated = [...arr];
              updated[index] = { ...item, ...newData };
              return updated;
            }
            return arr;
          }
          return [...arr, newData];
        });
        this.formKnowledge.markAsDirty();
        syncDataSource();
        return data();
      },
      delete: (item: T) => {
        this.formKnowledge.markAsDirty();
        data.update((arr) => arr.filter((i) => i !== item));
        syncDataSource();
        return data();
      },
      set: (items: T[]) => {
        data.set(items);
        syncDataSource();
      },
    };
  }

  private handleResourceDialog<T>(
    component: any,
    resourceManager: ReturnType<typeof this.createResourceManager<T>>,
    formField: string,
    item?: T,
  ) {
    const dataProperty = formField || 'data';

    const dialogRef = this._dialog.open(component, {
      size: 'm',
      disableClose: true,
      data: { [dataProperty]: item },
    });

    // attach reusable guard
    attachDirtyDialogGuard(dialogRef);

    dialogRef.afterClosed().subscribe({
      next: (data) => {
        if (data) {
          const updated = resourceManager.update(item ?? null, data);
          this.formKnowledge.patchValue({ [formField]: updated });
          this._analyticsService.invalidateKnowledgeCache();
        }
      },
    });
  }

  handleUserInstructionDialog(userInstruction?: KnowledgeUserInstruction) {
    this.handleResourceDialog(
      FocusUserInstructionDialogComponent,
      this.userInstructions,
      'userInstructions',
      userInstruction,
    );
  }

  handleBestPracticeDialog(bestPractice?: KnowledgeBestPractice) {
    this.handleResourceDialog(
      FocusBestPracticeDialogComponent,
      this.bestPractices,
      'bestPractices',
      bestPractice,
    );
  }

  handleLearningResourceDialog(learningResource?: KnowledgeLearningResource) {
    this.handleResourceDialog(
      FocusLearningResourceDialogComponent,
      this.learningResources,
      'learningResources',
      learningResource,
    );
  }

  handleTechnicalResourceDialog(
    technicalResource?: KnowledgeTechnicalResource,
  ) {
    this.handleResourceDialog(
      FocusTechnicalResourceDialogComponent,
      this.technicalResources,
      'technicalResources',
      technicalResource,
    );
  }

  handleIntegrationDialog(integration?: KnowledgeIntegration) {
    this.handleResourceDialog(
      FocusIntegrationDialogComponent,
      this.integrations,
      'integrations',
      integration,
    );
  }

  handleFaqDialog(faq?: KnowledgeFaq) {
    this.handleResourceDialog(FocusFaqDialogComponent, this.faq, 'faq', faq);
  }

  handleAdditionalResourceDialog(
    additionalResource?: KnowledgeAdditionalResource,
  ) {
    this.handleResourceDialog(
      FocusAdditionalResourceDialogComponent,
      this.additionalResources,
      'additionalResources',
      additionalResource,
    );
  }

  handleSuccessStoryDialog(successStory?: KnowledgeSuccessStory) {
    this.handleResourceDialog(
      FocusSuccessStoryDialogComponent,
      this.successStories,
      'successStories',
      successStory,
    );
  }

  handleReleaseNoteDialog(releaseNote?: KnowledgeReleaseNote) {
    this.handleResourceDialog(
      FocusReleaseNoteDialogComponent,
      this.releaseNotes,
      'releaseNotes',
      releaseNote,
    );
  }

  handleUserInstructionDelete(userInstruction: KnowledgeUserInstruction) {
    this._uiService
      .showDeleteDialog(
        POPUPS.DELETE.TITLE,
        POPUPS.DELETE.DESCRIPTION,
        POPUPS.DELETE.WARNING,
        BUTTON_TEXTS.DELETE,
        BUTTON_TEXTS.CANCEL,
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const updated = this.userInstructions.delete(userInstruction);
          this.formKnowledge.patchValue({ userInstructions: updated });
          this._analyticsService.invalidateKnowledgeCache();
        }
      });
  }

  handleBestPracticeDelete(bestPractice: KnowledgeBestPractice) {
    this._uiService
      .showDeleteDialog(
        POPUPS.DELETE.TITLE,
        POPUPS.DELETE.DESCRIPTION,
        POPUPS.DELETE.WARNING,
        BUTTON_TEXTS.DELETE,
        BUTTON_TEXTS.CANCEL,
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const updated = this.bestPractices.delete(bestPractice);
          this.formKnowledge.patchValue({ bestPractices: updated });
          this._analyticsService.invalidateKnowledgeCache();
        }
      });
  }

  handleLearningResourceDelete(learningResource: KnowledgeLearningResource) {
    this._uiService
      .showDeleteDialog(
        POPUPS.DELETE.TITLE,
        POPUPS.DELETE.DESCRIPTION,
        POPUPS.DELETE.WARNING,
        BUTTON_TEXTS.DELETE,
        BUTTON_TEXTS.CANCEL,
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const updated = this.learningResources.delete(learningResource);
          this.formKnowledge.patchValue({ learningResources: updated });
          this._analyticsService.invalidateKnowledgeCache();
        }
      });
  }

  handleTechnicalResourceDelete(technicalResource: KnowledgeTechnicalResource) {
    this._uiService
      .showDeleteDialog(
        POPUPS.DELETE.TITLE,
        POPUPS.DELETE.DESCRIPTION,
        POPUPS.DELETE.WARNING,
        BUTTON_TEXTS.DELETE,
        BUTTON_TEXTS.CANCEL,
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const updated = this.technicalResources.delete(technicalResource);
          this.formKnowledge.patchValue({ technicalResources: updated });
          this._analyticsService.invalidateKnowledgeCache();
        }
      });
  }

  handleIntegrationDelete(integration: KnowledgeIntegration) {
    this._uiService
      .showDeleteDialog(
        POPUPS.DELETE.TITLE,
        POPUPS.DELETE.DESCRIPTION,
        POPUPS.DELETE.WARNING,
        BUTTON_TEXTS.DELETE,
        BUTTON_TEXTS.CANCEL,
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const updated = this.integrations.delete(integration);
          this.formKnowledge.patchValue({ integrations: updated });
          this._analyticsService.invalidateKnowledgeCache();
        }
      });
  }

  handleFaqDelete(faq: KnowledgeFaq) {
    this._uiService
      .showDeleteDialog(
        POPUPS.DELETE.TITLE,
        POPUPS.DELETE.DESCRIPTION,
        POPUPS.DELETE.WARNING,
        BUTTON_TEXTS.DELETE,
        BUTTON_TEXTS.CANCEL,
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const updated = this.faq.delete(faq);
          this.formKnowledge.patchValue({ faq: updated });
          this._analyticsService.invalidateKnowledgeCache();
        }
      });
  }

  handleAdditionalResourceDelete(
    additionalResource: KnowledgeAdditionalResource,
  ) {
    this._uiService
      .showDeleteDialog(
        POPUPS.DELETE.TITLE,
        POPUPS.DELETE.DESCRIPTION,
        POPUPS.DELETE.WARNING,
        BUTTON_TEXTS.DELETE,
        BUTTON_TEXTS.CANCEL,
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const updated = this.additionalResources.delete(additionalResource);
          this.formKnowledge.patchValue({ additionalResources: updated });
          this._analyticsService.invalidateKnowledgeCache();
        }
      });
  }

  handleSuccessStoryDelete(successStory: KnowledgeSuccessStory) {
    this._uiService
      .showDeleteDialog(
        POPUPS.DELETE.TITLE,
        POPUPS.DELETE.DESCRIPTION,
        POPUPS.DELETE.WARNING,
        BUTTON_TEXTS.DELETE,
        BUTTON_TEXTS.CANCEL,
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const updated = this.successStories.delete(successStory);
          this.formKnowledge.patchValue({ successStories: updated });
          this._analyticsService.invalidateKnowledgeCache();
        }
      });
  }

  handleReleaseNoteDelete(releaseNote: KnowledgeReleaseNote) {
    this._uiService
      .showDeleteDialog(
        POPUPS.DELETE.TITLE,
        POPUPS.DELETE.DESCRIPTION,
        POPUPS.DELETE.WARNING,
        BUTTON_TEXTS.DELETE,
        BUTTON_TEXTS.CANCEL,
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const updated = this.releaseNotes.delete(releaseNote);
          this.formKnowledge.patchValue({ releaseNotes: updated });
          this._analyticsService.invalidateKnowledgeCache();
        }
      });
  }

  private handleDragDrop<T>(
    event: CdkDragDrop<T[]>,
    source: {
      dataSource: { data: T[] };
      set: (data: T[]) => void;
    },
    formControlName: string,
  ): void {
    const data = [...source.dataSource.data];

    const draggedItem = event.item.data as T;
    const previousIndex = data.indexOf(draggedItem);
    const currentIndex = event.currentIndex;

    if (previousIndex === -1 || previousIndex === currentIndex) return;

    // Move the item
    moveItemInArray(data, previousIndex, currentIndex);

    // Reassign displayOrder
    for (const [index, item] of data.entries()) {
      (item as any).displayOrder = index + 1;
    }

    // Sort by displayOrder to always display top-to-bottom
    data.sort(
      (a, b) => ((a as any).displayOrder ?? 0) - ((b as any).displayOrder ?? 0),
    );

    // Update datasource and form
    source.dataSource.data = data;
    source.set(data);
    this.formKnowledge.patchValue({ [formControlName]: data });
    this.formKnowledge.markAsDirty();

  }

  dragDropUserInstruction(event: CdkDragDrop<KnowledgeUserInstruction[]>) {
    this.handleDragDrop(event, this.userInstructions, 'userInstructions');
  }

  dragDropBestPractice(event: CdkDragDrop<KnowledgeBestPractice[]>) {
    this.handleDragDrop(event, this.bestPractices, 'bestPractices');
  }

  dragDropLearningResources(event: CdkDragDrop<KnowledgeLearningResource[]>) {
    this.handleDragDrop(event, this.learningResources, 'learningResources');
  }

  dragDropTechnicalResources(event: CdkDragDrop<KnowledgeTechnicalResource[]>) {
    this.handleDragDrop(event, this.technicalResources, 'technicalResources');
  }

  dragDropIntegrations(event: CdkDragDrop<KnowledgeIntegration[]>) {
    this.handleDragDrop(event, this.integrations, 'integrations');
  }

  dragDropFaq(event: CdkDragDrop<KnowledgeFaq[]>) {
    this.handleDragDrop(event, this.faq, 'faq');
  }

  dragDropSuccessStory(event: CdkDragDrop<KnowledgeSuccessStory[]>) {
    this.handleDragDrop(event, this.successStories, 'successStories');
  }

  dragDropAdditionalResources(
    event: CdkDragDrop<KnowledgeAdditionalResource[]>,
  ) {
    this.handleDragDrop(event, this.additionalResources, 'additionalResources');
  }

  dragDropReleaseNote(event: CdkDragDrop<KnowledgeReleaseNote[]>) {
    this.handleDragDrop(event, this.releaseNotes, 'releaseNotes');
  }

  handleUnpublishedConfirmation(): Promise<boolean> {
    return firstValueFrom(
      this._uiService
        .showUnpublishedConfrimDialog(
          POPUPS.UNPUBLISHED_CONFIRMATION.TITLE,
          POPUPS.UNPUBLISHED_CONFIRMATION.DESCRIPTION,
          POPUPS.UNPUBLISHED_CONFIRMATION.WARNING,
          BUTTON_TEXTS.UNPUBLISH,
          BUTTON_TEXTS.CANCEL,
        )
        .afterClosed(),
    ).then((confirmed) => !!confirmed);
  }

  private updatePublishedState<T extends { published: boolean }>(
    element: T,
    published: boolean,
    dataSignal: { data: () => T[]; set: (val: T[]) => void },
    formControlName: string,
  ) {
    const updated = dataSignal
      .data()
      .map((item) => (item === element ? { ...item, published } : item));

    dataSignal.set(updated);
    this.formKnowledge.patchValue(
      { [formControlName]: updated },
      { emitEvent: false },
    );
  }

  async toggleRowPublished<T extends { published: boolean; title?: string }>(
    element: T,
    event: Event,
    dataSignal: { data: () => T[]; set: (val: T[]) => void },
    formControlName: string,
  ) {
    const checked = (event.target as HTMLInputElement).checked;

    // Confirm only when unpublishing
    if (!checked && element.published) {
      const confirmed = await this.handleUnpublishedConfirmation();

      if (!confirmed) {
        this.updatePublishedState(element, true, dataSignal, formControlName);
        return;
      }
    }

    // Apply change
    this.updatePublishedState(element, checked, dataSignal, formControlName);
  }

  handleDraftSave() {
    const knowledgeUuid = this.knowledgeUuid();
    const offering = this.offering();
    const knowledge = this.knowledge();

    if (offering === null) return;

    if (offering.publishedAt == null) {
      const offeringTypeRoute =
        OFFERING_TYPE_ROUTE[offering.offeringType ?? ''] ?? 'application';
      this._uiService.showErrorAlert(
        POPUPS.OFFERING_VALIDATION.TITLE,
        POPUPS.OFFERING_VALIDATION.DESCRIPTION,
        `/focus/offering/${offeringTypeRoute}/${offering.uuid}`,
        BUTTON_TEXTS.BACK_TO_OFFERING,
        BUTTON_TEXTS.CLOSE,
      );
      return;
    }

    const payload = buildMulesoftKnowledgeDTO(
      knowledge ?? {},
      this.formKnowledge,
      offering.uuid,
      offering.displayName || '',
      false,
    );
    const knowledgeId = this.knowledge()?.id;
    // Edit path keys on the numeric knowledge id; save is disabled until it
    // loads, so this guard is a defensive no-op.
    if (knowledgeUuid && !knowledgeId) return;

    this.isDraftSaving.set(true);

    const request$ = knowledgeId
      ? this._mulesoftService.editKnowledge(payload, knowledgeId)
      : this._mulesoftService.createKnowledge(payload);

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        this.isDraftSaving.set(false);
        this.formKnowledge.markAsPristine();
        this.mergeSavedKnowledge(offering, response);
        this._uiService.showSuccessToast(
          'Your knowledge content has been saved',
          'Success',
        );

        // Analytics: draft save
        this._analyticsService.pushKnowledgeDraftSaveEvent(
          offering?.uuid || '',
          knowledge?.uuid || '',
          this.getCurrentKnowledgeValues(),
        );

        this.markKnowledgeAsSavedOrPublished();

        if (!knowledgeUuid && response?.knowledge?.uuid) {
          this.knowledge.update(k => ({
            ...k,
            id: response.knowledge.id,
            uuid: response.knowledge.uuid,
          }));
          this._localKnowledgeUuid.set(response.knowledge.uuid);
          this._location.replaceState(`/focus/knowledge/${response.knowledge.uuid}`);
        }
      },

      error: (err: Error) => {
        this.isDraftSaving.set(false);
        if (isDuplicateNameError(err)) {
          this._uiService.showErrorAlert(
            POPUPS.DUPLICATE_NAME.TITLE,
            POPUPS.DUPLICATE_NAME.KNOWLEDGE_DESCRIPTION,
          );
        } else {
          this._uiService.showErrorAlert(
            'Error',
            'There was an error saving your knowledge',
            '/focus/my-offerings',
            BUTTON_TEXTS.BACK_TO_MY_OFFERINGS,
            BUTTON_TEXTS.CLOSE,
          );
        }
        console.error('MuleSoft draft save error:', err);
      },
    });
  }

  handlePublish() {
    const knowledgeUuid = this.knowledgeUuid();
    const offering = this.offering();
    const knowledge = this.knowledge();

    if (this.formKnowledge.invalid) {
      const errorSections = this.getErrorSections();

      // Analytics: form error
      this._analyticsService.pushKnowledgeFormErrorEvent(
        offering?.uuid || '',
        knowledge?.uuid || '',
        this.getCurrentKnowledgeValues(),
        errorSections,
      );

      this.showValidationErrors();
      return;
    }

    if (offering === null) return;

    if (offering.publishedAt == null) {
      const offeringTypeRoute =
        OFFERING_TYPE_ROUTE[offering.offeringType ?? ''] ?? 'application';
      this._uiService.showErrorAlert(
        POPUPS.OFFERING_VALIDATION.TITLE,
        POPUPS.OFFERING_VALIDATION.DESCRIPTION,
        `/focus/offering/${offeringTypeRoute}/${offering.uuid}`,
        BUTTON_TEXTS.BACK_TO_OFFERING,
        BUTTON_TEXTS.CLOSE,
      );
      return;
    }

    const payload = buildMulesoftKnowledgeDTO(
      knowledge ?? {},
      this.formKnowledge,
      offering.uuid,
      offering.displayName || '',
      true,
    );
    const knowledgeId = this.knowledge()?.id;
    // Edit path keys on the numeric knowledge id; save is disabled until it
    // loads, so this guard is a defensive no-op.
    if (knowledgeUuid && !knowledgeId) return;

    this.isPublishing.set(true);

    const request$ = knowledgeId
      ? this._mulesoftService.editKnowledge(payload, knowledgeId)
      : this._mulesoftService.createKnowledge(payload);

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        this.isPublishing.set(false);
        this.formKnowledge.markAsPristine();
        this.mergeSavedKnowledge(offering, response);
        // Analytics: success
        this._analyticsService.pushKnowledgeFormSuccessEvent(
          offering?.uuid || '',
          this.knowledge()?.uuid || '',
          this.getCurrentKnowledgeValues(),
        );
        this.markKnowledgeAsSavedOrPublished();
        this._uiService.showSuccessAlert(
          POPUPS.SUCCESS.TITLE,
          POPUPS.SUCCESS.DESCRIPTION,
          '/focus/my-offerings',
          BUTTON_TEXTS.BACK_TO_MY_OFFERINGS,
          BUTTON_TEXTS.CLOSE,
        );

        if (!knowledgeUuid && response?.knowledge?.uuid) {
          this.knowledge.update(k => ({
            ...k,
            id: response.knowledge.id,
            uuid: response.knowledge.uuid,
          }));
          this._localKnowledgeUuid.set(response.knowledge.uuid);
          this._location.replaceState(`/focus/knowledge/${response.knowledge.uuid}`);
        }
      },
      error: (err: Error) => {
        this.isPublishing.set(false);
        if (isDuplicateNameError(err)) {
          this._uiService.showErrorAlert(
            POPUPS.DUPLICATE_NAME.TITLE,
            POPUPS.DUPLICATE_NAME.KNOWLEDGE_DESCRIPTION,
          );
        } else {
          this._uiService.showErrorAlert(
            'Error',
            'There was an error publishing your knowledge',
            '/focus/my-offerings',
            BUTTON_TEXTS.BACK_TO_MY_OFFERINGS,
            BUTTON_TEXTS.CLOSE,
          );
        }
        console.error('MuleSoft publish error:', err);
      },
    });
  }

  private conditionalArrayValidator(): ValidatorFn {
    return (form: AbstractControl): ValidationErrors | null => {
      const formGroup = form as FormGroup;
      const errors: any = {};

      // Validate sections with long descriptions
      this.validateSectionWithDescription(
        formGroup,
        'integrationsVisible',
        'integrations',
        'integrationLongDescription',
        'integrationsRequired',
        'integrationLongDescriptionRequired',
        errors,
      );

      // Validate simple array sections
       this.validateArraySection(
        formGroup,
        'userInstructionsVisible',
        'userInstructions',
        'userInstructionsRequired',
        errors,
      );
      this.validateArraySection(
        formGroup,
        'bestPracticesVisible',
        'bestPractices',
        'bestPracticesRequired',
        errors,
      );
      this.validateArraySection(
        formGroup,
        'learningResourcesVisible',
        'learningResources',
        'learningResourcesRequired',
        errors,
      );
      this.validateArraySection(
        formGroup,
        'technicalResourcesVisible',
        'technicalResources',
        'technicalResourcesRequired',
        errors,
      );
      this.validateArraySection(
        formGroup,
        'faqVisible',
        'faq',
        'faqRequired',
        errors,
      );
      this.validateArraySection(
        formGroup,
        'additionalResourcesVisible',
        'additionalResources',
        'additionalResourcesRequired',
        errors,
      );
      this.validateArraySection(
        formGroup,
        'successStoriesVisible',
        'successStories',
        'successStoriesRequired',
        errors,
      );
      this.validateArraySection(
        formGroup,
        'releaseNotesVisible',
        'releaseNotes',
        'releaseNotesRequired',
        errors,
      );

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  private validateSectionWithDescription(
    formGroup: FormGroup,
    visibilityKey: string,
    arrayKey: string,
    descriptionKey: string,
    arrayErrorKey: string,
    descriptionErrorKey: string,
    errors: any,
  ): void {
    const isVisible = formGroup.get(visibilityKey)?.value;
    const descControl = formGroup.get(descriptionKey);

    if (isVisible) {
      const array = formGroup.get(arrayKey)?.value;
      if (!array || array.length === 0) {
        errors[arrayErrorKey] = true;
      }

      const description = descControl?.value;
      const stripped = description ? description.replace(/<[^>]*>/g, '').trim() : '';
      if (!stripped) {
        errors[descriptionErrorKey] = true;
        descControl?.setErrors({ required: true });
      } else {
        descControl?.setErrors(null);
      }
    } else {
      descControl?.setErrors(null);
    }
  }

  private validateArraySection(
    formGroup: FormGroup,
    visibilityKey: string,
    arrayKey: string,
    errorKey: string,
    errors: any,
  ): void {
    if (formGroup.get(visibilityKey)?.value) {
      const array = formGroup.get(arrayKey)?.value;
      if (!array || array.length === 0) {
        errors[errorKey] = true;
      }
    }
  }

  private showValidationErrors() {
    this.formKnowledge.markAllAsTouched();

    const formErrors = this.formKnowledge.errors;
    if (!formErrors) return;

    const errorMessages = this.collectErrorMessages(formErrors);

    const errorMessage = this.formatErrorMessage(errorMessages);

    this._uiService.showErrorAlert(
      KNOWLEDGE_FORM_TEXTS.ERRORS.VALIDATION,
      errorMessage,
    );
  }

  private collectErrorMessages(formErrors: ValidationErrors): string[] {
    const errorMapping = this.getErrorMapping();

    return Object.keys(formErrors)
      .filter((errorKey) => errorMapping[errorKey])
      .map((errorKey) => errorMapping[errorKey]);
  }

  private getErrorMapping(): Record<string, string> {
    const errors = KNOWLEDGE_FORM_TEXTS.ERRORS;

    return {
      userInstructionsRequired: errors.USER_INSTRUCTIONS_REQUIRED,
      bestPracticesRequired: errors.BEST_PRACTICES_REQUIRED,
      learningResourcesRequired: errors.LEARNING_RESOURCES_REQUIRED,
      technicalResourcesRequired: errors.TECHNICAL_RESOURCES_REQUIRED,
      integrationsRequired: errors.INTEGRATIONS_REQUIRED,
      integrationLongDescriptionRequired:
        errors.INTEGRATION_LONG_DESCRIPTION_REQUIRED,
      faqRequired: errors.FAQ_REQUIRED,
      additionalResourcesRequired: errors.ADDITIONAL_RESOURCES_REQUIRED,
      successStoriesRequired: errors.SUCCESS_STORIES_REQUIRED,
      releaseNotesRequired: errors.RELEASE_NOTES_REQUIRED,
    };
  }

  private formatErrorMessage(errorMessages: string[]): string {
    return `${KNOWLEDGE_FORM_TEXTS.ERRORS.HEADER}\n\n${errorMessages.join(
      '\n',
    )}\n\n${KNOWLEDGE_FORM_TEXTS.ERRORS.MESSAGE}`;
  }

  //Tracking
  private setupRouteChangeTracking(): void {
    this._router.events
      .pipe(
        filter(
          (event): event is NavigationStart => event instanceof NavigationStart,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.trackKnowledgeAbandonNavigate());
  }
  private setInitialKnowledgeValues(): void {
    this._analyticsService.setOriginalKnowledgeValues(
      this.getCurrentKnowledgeValues(),
    );
  }

  private getCurrentKnowledgeValues(): any {
    const formValue = this.formKnowledge.getRawValue();

    return {
      sections: [
        {
          name: 'User instructions',
          isVisible: formValue.userInstructionsVisible,
          rowCount: this.userInstructions.data().length,
          description: formValue.userInstructionLongDescription || '',
          data: this.userInstructions.data(), // Add actual data
        },
        {
          name: 'Best practices',
          isVisible: formValue.bestPracticesVisible,
          rowCount: this.bestPractices.data().length,
          data: this.bestPractices.data(),
        },
        {
          name: 'Authoritative resources',
          isVisible: formValue.learningResourcesVisible,
          rowCount: this.learningResources.data().length,
          data: this.learningResources.data(),
        },
        {
          name: 'Technical resources',
          isVisible: formValue.technicalResourcesVisible,
          rowCount: this.technicalResources.data().length,
          data: this.technicalResources.data(),
        },
        {
          name: 'Integrations',
          isVisible: formValue.integrationsVisible,
          rowCount: this.integrations.data().length,
          description: formValue.integrationLongDescription || '',
          data: this.integrations.data(),
        },
        {
          name: 'FAQ',
          isVisible: formValue.faqVisible,
          rowCount: this.faq.data().length,
          data: this.faq.data(),
        },
        {
          name: 'Additional resources',
          isVisible: formValue.additionalResourcesVisible,
          rowCount: this.additionalResources.data().length,
          data: this.additionalResources.data(),
        },
        {
          name: 'Success stories',
          isVisible: formValue.successStoriesVisible,
          rowCount: this.successStories.data().length,
          data: this.successStories.data(),
        },
        {
          name: 'Release notes',
          isVisible: formValue.releaseNotesVisible,
          rowCount: this.releaseNotes.data().length,
          data: this.releaseNotes.data(),
        },
      ],
    };
  }

  private getErrorSections(): string[] {
    const formErrors = this.formKnowledge.errors;
    if (!formErrors) return [];

    const errorMapping: Record<string, string> = {
      userInstructionsRequired: 'User instructions',
      bestPracticesRequired: 'Best practices',
      learningResourcesRequired: 'Learning resources',
      technicalResourcesRequired: 'Technical resources',
      integrationsRequired: 'Integrations',
      integrationLongDescriptionRequired: 'Integrations',
      faqRequired: 'FAQ',
      additionalResourcesRequired: 'Additional resources',
      successStoriesRequired: 'Success stories',
      releaseNotesRequired: 'Release notes',
    };

    const errorSections = new Set<string>();
    const errorKeys = Object.keys(formErrors);
    for (const errorKey of errorKeys) {
      if (errorMapping[errorKey]) {
        errorSections.add(errorMapping[errorKey]);
      }
    }

    return Array.from(errorSections);
  }

  private trackKnowledgeAbandonNavigate(): void {
    const offering = this.offering();
    if (
      this._isKnowledgeAbandonNavigateLogged ||
      this._isKnowledgeSavedOrPublished
    ) {
      return;
    }

    if (!this.formKnowledge.dirty) {
      return;
    }

    this._isKnowledgeAbandonNavigateLogged = true;

    this._analyticsService.pushKnowledgeAbandonNavigateEvent(
      offering?.uuid || '',
      this.knowledge()?.uuid || '',
      this.getCurrentKnowledgeValues(),
    );
  }

  private trackKnowledgeAbandonClose(): void {
    const offering = this.offering();
    if (
      this._isKnowledgeAbandonCloseLogged ||
      this._isKnowledgeSavedOrPublished
    ) {
      return;
    }

    if (!this.formKnowledge.dirty) {
      return;
    }

    this._isKnowledgeAbandonCloseLogged = true;

    this._analyticsService.pushKnowledgeAbandonCloseEvent(
      offering?.uuid || '',
      this.knowledge()?.uuid || '',
      this.getCurrentKnowledgeValues(),
    );
  }

  getContentStatusLabel(publishedAt?: string | null, updatedAt?: string | null): string {
    // 1. Draft: The knowledge has never been published
    if (!publishedAt) {
      return 'Draft';
    }

    const pubTime = new Date(publishedAt).getTime();
    const updTime = updatedAt ? new Date(updatedAt).getTime() : 0;

    // 2. Has changes: Published, but updated more recently
    if (updTime > pubTime) {
      return 'Has changes';
    }

    // 3. Published: Published, with no newer updates pending
    return 'Published';
  }
}
